import { Card, CardContent, Paper, Typography } from '@mui/material';
import moment from 'moment';
import { FC } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';
import styles from './styles';
import { GraphDataProps } from './types';

// Customized tooltip
export const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <Card sx={styles.card} data-testid="tooltip">
                <CardContent>
                    <Typography sx={styles.tooltipLabel}>Date</Typography>
                    <Typography variant="h5" component="div" sx={styles.tooltipValue}>
                        {moment(label).format('MMMM DD, YYYY')}
                    </Typography>
                    <Typography sx={[styles.tooltipLabel, { mt: '8px' }]}>Active Members</Typography>
                    <Typography variant="body2" sx={styles.tooltipValue}>
                        {payload[0].value}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return null;
};

// This function will give x axis labels based on data group by month
const getTicks = (startDate: Date, endDate: Date, num: number) => {
    const diffDays = moment(endDate).diff(moment(startDate), 'days');
    const current = startDate;
    const velocity = Math.round(diffDays / (num - 1));

    const ticks = [startDate.getTime()];
    for (let i = 1; i < num - 1; i += 1) {
        ticks.push(
            moment(current)
                .add({ days: i * velocity })
                .valueOf(),
        );
    }

    ticks.push(moment(endDate).valueOf());

    return ticks;
};

const Graph: FC<GraphDataProps> = ({ data }) => {
    const startDate = new Date(data[0]?.name);
    const endDate = new Date(data[data.length - 1]?.name);
    const domain = [() => startDate.getTime(), () => endDate.getTime()];
    const ticks = getTicks(startDate, endDate, 12);
    const filledData = data.map((val) => {
        return { ...val, name: new Date(val.name).getTime() };
    });
    const dataMax = Math.max(...data.map((key) => key.activeMembers));

    return (
        <Paper elevation={0} sx={styles.graph}>
            <Typography id="total_members_title" component="header" sx={styles.title}>
                Total Members
            </Typography>
            <Typography id="over_time_title" component="sub" sx={styles.subTitle}>
                {filledData.length ? 'Over Time' : ''}
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={filledData} margin={styles.graphMargins}>
                    <defs>
                        <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="20%" stopColor="#9CB8FF" stopOpacity={0.5} />
                            <stop offset="80%" stopColor="#9CB8FF" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="1 1" stroke="#E0E4ED" />
                    <XAxis
                        dataKey="name"
                        scale="time"
                        type="number"
                        tickFormatter={(date) => {
                            if (
                                moment(new Date(date)).year() === moment().year() &&
                                moment(new Date(date)).month() === 0
                            ) {
                                return moment(new Date(date)).format('MMM YYYY');
                            }
                            return moment(new Date(date)).format('MMM');
                        }}
                        tickLine={false}
                        axisLine={{ stroke: '#E0E4ED', strokeWidth: 2 }}
                        ticks={ticks}
                        domain={domain as unknown as AxisDomain} // Define range b/w start point and end point
                    />
                    <YAxis
                        tickLine={false}
                        axisLine={{ stroke: '#E0E4ED', strokeWidth: 2 }}
                        allowDecimals={false}
                        allowDataOverflow
                        width={25 + Math.floor(Math.log10(dataMax)) * 10}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Area
                        type="monotone"
                        dataKey="activeMembers"
                        stroke="#4AA6E1"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorPv)"
                        activeDot={{ r: 4, fill: '#ffffff', stroke: '#4AA6E1' }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Paper>
    );
};

export default Graph;
